import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { ApiClientError } from "@chaochud/api-client";

import { Logo } from "@/components/logo";
import { adminAuthClient } from "@/lib/api-clients";
import { useAdminAuth } from "@/lib/auth/auth-context";

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiClientError) {
    switch (error.code) {
      case "MFA_INVALID_CODE":
        return "รหัสยืนยันไม่ถูกต้อง";
      case "MFA_TOKEN_INVALID":
        return "เซสชันยืนยันตัวตนหมดอายุ กรุณาเข้าสู่ระบบใหม่";
      case "RATE_LIMIT_EXCEEDED":
        return "มีการเรียกใช้งานบ่อยเกินไป กรุณาลองใหม่ในอีกสักครู่";
      default:
        return error.message || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง";
    }
  }
  return "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง";
}

export function MfaVerifyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { completeSession, isAuthenticated, isHydrated } = useAdminAuth();
  const mfaToken = (location.state as { mfaToken?: string } | null)?.mfaToken;
  const [code, setCode] = useState("");
  const [backupCode, setBackupCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (isHydrated && isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  if (!mfaToken) {
    return <Navigate to="/login" replace />;
  }

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-6 bg-slate-bg p-6">
      <Logo iconSize={32} textClassName="text-2xl" />
      <div className="w-full max-w-sm rounded-xl border border-slate-line bg-slate-surface p-6 shadow-sm">
        <h2 className="mb-2 font-heading text-lg font-semibold text-ink">ยืนยันตัวตนสองขั้นตอน</h2>
        <p className="mb-4 text-sm text-muted">
          {useBackup
            ? "กรอกรหัสสำรอง (backup code) ที่ได้รับตอนเปิดใช้ MFA"
            : "กรอกรหัส 6 หลักจากแอป Authenticator"}
        </p>
        <form
          className="flex flex-col gap-3"
          onSubmit={async (event) => {
            event.preventDefault();
            setServerError(null);
            setSubmitting(true);
            try {
              const session = useBackup
                ? await adminAuthClient.verifyMfaBackup({ mfaToken, backupCode })
                : await adminAuthClient.verifyMfa({ mfaToken, code });
              completeSession(session);
              navigate("/dashboard");
            } catch (error) {
              setServerError(toErrorMessage(error));
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {useBackup ? (
            <input
              value={backupCode}
              onChange={(event) => setBackupCode(event.target.value)}
              className="rounded-lg border border-slate-line px-3 py-2 text-sm"
              placeholder="backup code"
              autoComplete="one-time-code"
            />
          ) : (
            <input
              value={code}
              onChange={(event) => setCode(event.target.value)}
              className="rounded-lg border border-slate-line px-3 py-2 text-sm tracking-widest"
              placeholder="000000"
              inputMode="numeric"
              autoComplete="one-time-code"
            />
          )}
          {serverError ? <p className="text-sm text-error-text">{serverError}</p> : null}
          <button
            type="submit"
            disabled={submitting}
            className="rounded-lg bg-plum px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
          >
            ยืนยัน
          </button>
          <button
            type="button"
            className="text-sm text-muted underline"
            onClick={() => {
              setUseBackup((value) => !value);
              setServerError(null);
            }}
          >
            {useBackup ? "ใช้รหัสจากแอป Authenticator" : "ใช้รหัสสำรองแทน"}
          </button>
        </form>
      </div>
    </main>
  );
}
